import { defaultClient } from '@mint-sdk/api-core';
import { z } from 'zod';

import {
  AddMediaItemChildrenRequestSchema,
  AddMediaRowsRequestSchema,
  AssignAdAccountQuerySchema,
  AssignAdAccountResponseSchema,
  GetMediaItemChildOptionsResponseSchema,
  GetMediaItemDetailsResponseSchema,
  MediaItemChildOptionsSchema,
  MediaItemCurrencyRateQuerySchema,
  MediaItemCurrencyRateResponseSchema,
} from '../shared/schemas/mediaItem.js';

export type MediaItemChildOptions = z.infer<typeof MediaItemChildOptionsSchema>;

export type AddMediaRowsRequest = z.infer<typeof AddMediaRowsRequestSchema>;

export type AddMediaItemChildrenRequest = z.infer<
  typeof AddMediaItemChildrenRequestSchema
>;

export type GetMediaItemChildOptionsResponse = z.infer<
  typeof GetMediaItemChildOptionsResponseSchema
>;

export type GetMediaItemDetailsResponse = z.infer<
  typeof GetMediaItemDetailsResponseSchema
>;

export const getMediaItemChildOptionsQueryOptions = defaultClient.query({
  url: 'planning-service/media-items/$mediaItemId/child-options/' as const,
  method: 'GET',
  responseSchema: GetMediaItemChildOptionsResponseSchema,
  processResponse(data) {
    return data;
  },
});

export const useAddMediaItemChildren = defaultClient.mutation({
  url: 'planning-service/media-items/$mediaItemId/child-structure/' as const,
  method: 'POST',
  requestSchema: AddMediaItemChildrenRequestSchema,
  responseSchema: z.null(),
  processResponse(data) {
    return data;
  },
});

export const useAddMediaRows = defaultClient.mutation({
  url: 'planning-service/media-items/$mediaItemId/media-rows/' as const,
  method: 'POST',
  requestSchema: AddMediaRowsRequestSchema,
  responseSchema: z.null(),
  processResponse(data) {
    return data;
  },
});

export const useDuplicateMediaItem = defaultClient.mutation({
  url: 'planning-service/media-items/$mediaItemId/duplicate/' as const,
  method: 'POST',
  responseSchema: z.null(),
  requestSchema: z.object({
    mediaDataId: z.string(),
    numberDuplicates: z.number().min(1, 'Media rows count must be at least 1'),
  }),
  processResponse(data) {
    return data;
  },
});

export const deleteMediaItem = defaultClient.mutation({
  url: 'planning-service/media-items/$mediaItemId/' as const,
  method: 'DELETE',
  responseSchema: z.string(),
  processResponse(data) {
    return data;
  },
});

export const getMediaItemDetailsQueryOptions = defaultClient.query({
  url: 'planning-service/media-items/$mediaItemId/details/' as const,
  method: 'GET',
  responseSchema: GetMediaItemDetailsResponseSchema,
  processResponse(data) {
    return data;
  },
});

export const getMediaItemCurrencyRateQueryOptions = defaultClient.query({
  url: 'planning-service/media-items/$mediaItemId/currency-rate/' as const,
  method: 'GET',
  querySchema: MediaItemCurrencyRateQuerySchema,
  responseSchema: MediaItemCurrencyRateResponseSchema,
  processResponse(data) {
    return data.data?.items[0];
  },
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
});

export const updateAssignedAdAccount = defaultClient.mutation({
  url: 'planning-service/media-items/$mediaItemId/assign-ad-account/' as const,
  method: 'PATCH',
  responseSchema: AssignAdAccountResponseSchema,
  requestSchema: AssignAdAccountQuerySchema,
  processResponse(data) {
    return data;
  },
});
